<template>
  <div>
    <NavbarTwo />
    <div class="static-hero">
      <div class="container mw-1356">
        <span class="top-title">Installation Guide</span>
        <h1>How to Install Our IPTV STREAM+ Service</h1>
        <p>Get set up and streaming in minutes — follow the steps below for your device</p>
      </div>
    </div>

    <div class="static-body bg-gondola">
      <div class="container mw-1356" style="max-width: 960px">

        <!-- Download app -->
        <div class="download-box">
          <div class="download-info">
            <h2><i class="ti ti-brand-android"></i> Get the App (IBO Pro Player)</h2>
            <p>
              Download IBO Pro Player (APK) — the easiest way to watch on Android TV
              boxes, Fire TV and Android phones. Install it, then follow the
              <strong>IBO Pro Player</strong> setup steps below to add your playlist.
            </p>
          </div>
          <a :href="apkUrl" class="btn download-btn" target="_blank" rel="noopener" download>
            <i class="ti ti-download"></i> Download App (APK)
          </a>
        </div>

        <!-- General steps -->
        <div class="steps-list">
          <div class="step-card" v-for="(step, i) in generalSteps" :key="i">
            <div class="step-number">{{ i + 1 }}</div>
            <div class="step-content">
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </div>
          </div>
        </div>

        <!-- Need help -->
        <div class="help-box">
          <h3><i class="ti ti-headset"></i> Need help?</h3>
          <p>
            If you encounter any problems or have any questions, please contact our customer
            support for assistance. Our friendly and knowledgeable representatives are available
            to help you resolve any technical issues or answer any questions you may have.
          </p>
          <RouterLink to="/contact-us" class="btn help-btn">Contact Support</RouterLink>
        </div>

        <!-- Device selector -->
        <div class="device-section">
          <h2>Select Your Device</h2>
          <div class="device-tabs">
            <button
              v-for="device in devices"
              :key="device.id"
              class="device-tab"
              :class="{ active: activeDevice === device.id }"
              @click="activeDevice = device.id"
            >
              <i :class="device.icon"></i>
              {{ device.label }}
            </button>
          </div>

          <div class="device-guide">
            <h3>{{ currentDevice.guideTitle }}</h3>
            <ol class="guide-steps">
              <li v-for="(step, i) in currentDevice.steps" :key="i" v-html="step"></li>
            </ol>
            <p v-if="currentDevice.note" class="guide-note">
              <i class="ti ti-info-circle"></i> {{ currentDevice.note }}
            </p>
          </div>
        </div>

      </div>
    </div>
    <MainFooter />
    <CopyRight />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useSeo } from "@/composables/useSeo";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import CopyRight from "@/components/Layouts/CopyRight.vue";

const generalSteps = [
  {
    title: "Download an IPTV App",
    text: "Download an IPTV app like IPTV Smarters Pro or TiviMate. The apps are compatible with various devices, including smartphones, tablets, smart TVs and set-top boxes (you can find the installation guide for each device below).",
  },
  {
    title: "Subscribe to Our IPTV+ Service",
    text: "Subscribe to our IPTV service by visiting our homepage. Choose the plan that best suits your needs and enter your billing and payment information. Once your subscription is confirmed, you will receive your login information via email.",
  },
  {
    title: "Launch the IPTV App",
    text: "Launch the IPTV app on your device and enter the login information you received during the subscription process.",
  },
  {
    title: "Navigate to the Channel List",
    text: "Navigate to the channel list to select desired channels. The channels are organized by country and category, such as sports, news, entertainment and more.",
  },
  {
    title: "Enjoy Your IPTV Service",
    text: "Enjoy your IPTV service with high quality streaming. You can also access on-demand content, including movies and TV shows, by selecting the VOD category.",
  },
];

const devices = [
  {
    id: "android",
    label: "Smart TV / Android",
    icon: "ti ti-device-tv",
    guideTitle: "How to set up IPTV on Android with IPTV Smarters Player",
    steps: [
      'Start by downloading the IPTV Smarters app via this URL: <a href="https://www.iptvsmarters.com/smarters.apk" target="_blank" rel="noopener">https://www.iptvsmarters.com/smarters.apk</a>',
      "Once downloaded, open the app and choose “Login with Xtream Codes API”.",
      "Enter the login details that we have given you after you have obtained a subscription with us, then click on “ADD USER” to proceed to the next section.",
      "Wait a few seconds while the app loads your channels and content.",
      "Click on the “Live TV” icon on the home screen.",
      "Select the channel group you want to watch and move on to the next section.",
      "Click on the channel name, then double-click on the small preview screen to switch to full screen. Done — enjoy your streams!",
    ],
    note: "",
  },
  {
    id: "ibo",
    label: "IBO Pro Player",
    icon: "ti ti-device-tv-old",
    guideTitle: "How to set up IBO Pro Player with your M3U playlist",
    steps: [
      'Install IBO Pro Player on your device — download the APK from <a href="https://iboplayer.com/app_downloads/ibop.apk" target="_blank" rel="noopener">https://iboplayer.com/app_downloads/ibop.apk</a> (or use the “Download App” button at the top of this page). No activation needed — it just plays your playlist.',
      "Open the IBO Pro Player app.",
      "Choose <strong>Add Playlist</strong> (or Playlist / M3U URL).",
      "Give your list a name and paste the M3U URL we sent you by email after subscribing.",
      "Save, then open your list — your channels and VOD will load automatically.",
    ],
    note: "IBO Pro Player only plays your playlist — it does not include any channels itself. If you didn't receive your M3U URL, contact support and we'll resend it.",
  },
  {
    id: "firestick",
    label: "Fire TV Stick",
    icon: "ti ti-flame",
    guideTitle: "How to set up IPTV on Amazon Fire TV Stick",
    steps: [
      "From the Fire TV home screen, go to Find → Search and type “Downloader”, then install the Downloader app.",
      "Go to Settings → My Fire TV → Developer Options and enable “Install Unknown Apps” for Downloader.",
      'Open Downloader and enter the URL: <a href="https://www.iptvsmarters.com/smarters.apk" target="_blank" rel="noopener">https://www.iptvsmarters.com/smarters.apk</a>, then click Go.',
      "When the download finishes, click “Install”, then open IPTV Smarters.",
      "Choose “Login with Xtream Codes API” and enter the login details you received by email after subscribing.",
      "Click “ADD USER”, wait for your channels to load, then open “Live TV” and start watching.",
    ],
    note: "",
  },
  {
    id: "apple",
    label: "iPhone / iPad",
    icon: "ti ti-brand-apple",
    guideTitle: "How to set up IBO Player Pro on iPhone, iPad and Apple TV",
    steps: [
      "Open the App Store, search for <strong>IBO Player Pro</strong>, and install it. (iPhone can't install APKs, so iOS users get the app from the App Store.)",
      "Open the IBO Player Pro app. No activation needed — it just plays your playlist.",
      "Choose <strong>Add Playlist</strong> (or Playlist / M3U URL).",
      "Give your list a name and paste the M3U URL we sent you by email after subscribing.",
      "Save, then open your list — your channels and VOD will load automatically.",
    ],
    note: "Prefer Xtream Codes? You can also use “Smarters Player Lite” from the App Store and log in with the username, password and server URL from your welcome email.",
  },
  {
    id: "desktop",
    label: "Windows / Macbook",
    icon: "ti ti-device-laptop",
    guideTitle: "How to set up IPTV on Windows PC and Mac",
    steps: [
      'Download IPTV Smarters for desktop from <a href="https://www.iptvsmarters.com" target="_blank" rel="noopener">www.iptvsmarters.com</a> (available for both Windows and macOS), then install and open it.',
      "Choose “Login with Xtream Codes API” and enter the login details you received by email after subscribing.",
      "Click “ADD USER” and wait for your channels and VOD content to load.",
      "Open “Live TV” and start watching. You can also use VLC Media Player: open Media → Open Network Stream and paste the M3U playlist URL from your welcome email.",
    ],
    note: "",
  },
  {
    id: "mag",
    label: "Mag Box",
    icon: "ti ti-box",
    guideTitle: "How to set up IPTV on MAG devices (250/254/256/322...)",
    steps: [
      "Turn on your MAG box and go to Settings → System Settings → Servers → Portals.",
      "Set “Portal 1 Name” to GoPlayTV.",
      "In “Portal 1 URL”, enter the portal URL we sent you by email after subscribing.",
      "Save the settings and restart the box (unplug and reconnect the power).",
      "After the reboot, your channel list will load automatically — enjoy!",
    ],
    note: "MAG devices need a portal URL tied to your box's MAC address. If you didn't receive yours, contact support with your MAC address (printed on the sticker under the box).",
  },
];

export default defineComponent({
  name: "InstallationGuidePage",
  components: { NavbarTwo, MainFooter, CopyRight },
  setup() {
    useSeo({
      title: "Installation Guide — How to Install IPTV STREAM+",
      description: "Step-by-step guide to install GoPlayTv IPTV on Smart TV, Android, Fire TV Stick, Apple iOS, Windows, Mac and MAG boxes. Start streaming in minutes.",
      url: "/installation-guide",
      keywords: "IPTV installation guide, IPTV Smarters setup, install IPTV Fire Stick, IPTV on Smart TV, MAG box setup, GoPlayTv setup",
    });

    const activeDevice = ref("android");
    const currentDevice = computed(
      () => devices.find((d) => d.id === activeDevice.value) ?? devices[0]
    );

    const apkUrl = "https://iboplayer.com/app_downloads/ibop.apk";

    return { generalSteps, devices, activeDevice, currentDevice, apkUrl };
  },
});
</script>

<style scoped>
.static-hero {
  background: linear-gradient(135deg, #0a0102 0%, #1a0508 100%);
  padding: 100px 0 60px;
  border-bottom: 1px solid #2b1e1f;
  text-align: center;
}
.static-hero .top-title { display: inline-block; color: #c90e0e; font-size: 0.8rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 1rem; }
.static-hero h1 { color: #fff; font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 800; margin-bottom: 0.75rem; }
.static-hero p { color: #a0a0a0; }
.static-body { padding: 60px 0 80px; }

/* Download app */
.download-box { display: flex; align-items: center; justify-content: space-between; gap: 1.5rem; flex-wrap: wrap; background: linear-gradient(135deg, #1a0508, #2a0a0e); border: 1px solid #c90e0e; border-radius: 12px; padding: 1.75rem 2rem; margin-bottom: 2.5rem; }
.download-info { flex: 1 1 320px; }
.download-info h2 { color: #fff; font-size: 1.35rem; font-weight: 800; margin-bottom: 0.6rem; }
.download-info h2 i { color: #3ddc84; margin-right: 0.45rem; }
.download-info p { color: #b8b0b1; font-size: 0.9rem; line-height: 1.7; margin: 0; }
.download-btn { display: inline-flex; align-items: center; gap: 0.5rem; background: #c90e0e; color: #fff; font-weight: 700; font-size: 0.95rem; padding: 0.85rem 1.85rem; border-radius: 8px; white-space: nowrap; }
.download-btn i { font-size: 1.2rem; }
.download-btn:hover { background: #a50b0b; color: #fff; }

/* General steps */
.steps-list { display: flex; flex-direction: column; gap: 1rem; }
.step-card { display: flex; gap: 1.25rem; background: #1a0f10; border: 1px solid #2b1e1f; border-radius: 10px; padding: 1.5rem; }
.step-number { flex-shrink: 0; width: 44px; height: 44px; border-radius: 50%; background: #c90e0e; color: #fff; font-weight: 800; font-size: 1.1rem; display: flex; align-items: center; justify-content: center; }
.step-content h3 { color: #fff; font-size: 1.05rem; font-weight: 700; margin-bottom: 0.5rem; }
.step-content p { color: #a0a0a0; font-size: 0.9rem; line-height: 1.7; margin: 0; }

/* Help box */
.help-box { margin-top: 2.5rem; background: linear-gradient(135deg, #1a0508, #2a0a0e); border: 1px solid #c90e0e; border-radius: 10px; padding: 1.75rem; text-align: center; }
.help-box h3 { color: #fff; font-size: 1.15rem; font-weight: 700; margin-bottom: 0.75rem; }
.help-box h3 i { color: #c90e0e; margin-right: 0.4rem; }
.help-box p { color: #a0a0a0; font-size: 0.9rem; line-height: 1.7; max-width: 640px; margin: 0 auto 1.25rem; }
.help-btn { background: #c90e0e; color: #fff; font-weight: 600; padding: 0.6rem 1.75rem; border-radius: 6px; }
.help-btn:hover { background: #a50b0b; color: #fff; }

/* Device selector */
.device-section { margin-top: 3.5rem; }
.device-section h2 { color: #fff; font-size: 1.5rem; font-weight: 800; text-align: center; margin-bottom: 1.5rem; }
.device-tabs { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.6rem; margin-bottom: 1.75rem; }
.device-tab { display: inline-flex; align-items: center; gap: 0.45rem; background: #1a0f10; border: 1px solid #2b1e1f; color: #a0a0a0; font-weight: 600; font-size: 0.85rem; font-family: inherit; padding: 0.65rem 1.1rem; border-radius: 30px; cursor: pointer; transition: all 0.2s; }
.device-tab i { font-size: 1rem; }
.device-tab:hover { border-color: #c90e0e; color: #fff; }
.device-tab.active { background: #c90e0e; border-color: #c90e0e; color: #fff; }
.device-guide { background: #1a0f10; border: 1px solid #2b1e1f; border-radius: 10px; padding: 1.75rem; }
.device-guide h3 { color: #fff; font-size: 1.1rem; font-weight: 700; margin-bottom: 1.25rem; }
.guide-steps { color: #a0a0a0; font-size: 0.9rem; line-height: 1.8; padding-left: 1.25rem; margin: 0; display: flex; flex-direction: column; gap: 0.6rem; }
.guide-steps li::marker { color: #c90e0e; font-weight: 700; }
.guide-steps :deep(a) { color: #c90e0e; text-decoration: underline; word-break: break-all; }
.guide-note { margin: 1.25rem 0 0; color: #d0a060; font-size: 0.85rem; line-height: 1.6; }
.guide-note i { margin-right: 0.3rem; }

@media (max-width: 575px) {
  .step-card { padding: 1.1rem; gap: 0.9rem; }
  .step-number { width: 36px; height: 36px; font-size: 0.95rem; }
  .device-guide { padding: 1.25rem; }
}
</style>
